import React, { FC, useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { useTheme } from '@mui/material/styles'
import TextField from '@mui/material/TextField'
import IconButton from '@mui/material/IconButton'
import useMediaQuery from '@mui/material/useMediaQuery'
import Box from '@mui/material/Box'

import {
  getDateString,
  getDaysFromToday,
  getDaysFromDate,
} from '@kaidam/fc-shared/src/booking/utils'

import FastNextIcon from '@mui/icons-material/FastForward'
import FastPreviousIcon from '@mui/icons-material/FastRewind'
import NextIcon from '@mui/icons-material/ChevronRight'
import PreviousIcon from '@mui/icons-material/ChevronLeft'

import snackbarActions from '@kaidam/fc-shared/src/frontend/store/modules/snackbar'

interface DateNavigatorProps {
  value: string,
  earliest: string,
  latest: string,
  onChange: {
    (val: string): void,
  }
}

const DateNavigator: FC<React.PropsWithChildren<DateNavigatorProps>> = ({
  value,
  earliest,
  latest,
  onChange,
}) => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const isLarge = useMediaQuery(theme.breakpoints.up('md'))

  const daysFromEarliestBookable = useMemo(() => {
    return getDaysFromDate(value, earliest)
  }, [
    value,
    earliest,
  ])

  const daysFromLatestBookable = useMemo(() => {
    return getDaysFromDate(value, latest)
  }, [
    value,
    latest,
  ])

  const onMove = useCallback((days: number) => {
    const dt = new Date(value)
    dt.setTime(dt.getTime() + days * 24 * 60 * 60 * 1000)
    if(getDaysFromDate(getDateString(dt), earliest) < 0) {
      dispatch(snackbarActions.setError(`date cannot be before: ${earliest}`))
      return
    }
    else if(getDaysFromDate(getDateString(dt), latest) > 0) {
      dispatch(snackbarActions.setError(`we do not currently have any availaible slots after: ${latest}`))
      return
    }
    onChange(getDateString(dt))
  }, [
    value,
    earliest,
    latest,
  ])

  const back = (
    <Box sx={{ p: 2 }}>
      <IconButton
        disabled={ daysFromEarliestBookable <= 0 }
        onClick={ () => onMove(-7) }
        size="large">
        <FastPreviousIcon />
      </IconButton>
      <IconButton
        disabled={ daysFromEarliestBookable <= 0 }
        onClick={ () => onMove(-1) }
        size="large">
        <PreviousIcon />
      </IconButton>
    </Box>
  )

  const forward = (
    <Box sx={{ p: 2 }}>
      <IconButton
        disabled={ daysFromLatestBookable >= 0 }
        onClick={ () => onMove(1) }
        size="large">
        <NextIcon />
      </IconButton>
      <IconButton
        disabled={ daysFromLatestBookable >= 0 }
        onClick={ () => onMove(7) }
        size="large">
        <FastNextIcon />
      </IconButton>
    </Box>
  )

  return (
    <Box>
      <Box sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
      }}>
        {isLarge && back}
        <Box sx={{ mt: 0 }}>
          <TextField
            label="Date"
            type="date"
            value={value}
            onChange={(ev) => {
              if(getDaysFromDate(ev.target.value, earliest) < 0) {
                dispatch(snackbarActions.setError(`date cannot be before: ${earliest}`))
                onChange(earliest)
              }
              else if(getDaysFromDate(ev.target.value, latest) > 0) {
                dispatch(snackbarActions.setError(`we do not currently have any availaible slots after: ${latest}`))
                onChange(latest)
              }
              else {
                onChange(ev.target.value)
              }
            }}
            sx={{
              '& label': {
                left: '35px',
                whiteSpace: 'nowrap',
              },
              '& input': {
                textAlign: 'center',
              },
            }}
          />
        </Box>
        {isLarge && forward}
      </Box>
      {!isLarge && (
        <Box sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
          {back}
          {forward}
        </Box>
      )}
    </Box>
  )
}

export default DateNavigator